import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Lock, LogOut, RefreshCw, Inbox, Mail, Phone, ArrowLeft, Loader2,
  ListTodo, Users, KeyRound, Trash2, Plus, Save, Reply, Link2, FileText, Printer, Pencil, Calculator,
} from "lucide-react";
import { IMAGES, CONTACT } from "@/lib/site-data";
import { OfferEditor } from "@/components/admin/OfferEditor";
import { OfferSheet } from "@/components/admin/OfferSheet";
import { Accounting } from "@/components/admin/accounting/Accounting";
import { PRODUCT_OPTIONS, COLORS } from "@/lib/configurator-data";

const PRODUCT_NAMES = ["Fenster", "Haustüren", "Rollläden", "Sonnenschutz", "Insektenschutz", "Wintergarten", "Vordächer", "Schiebetüren", "Garagentore", "Zäune", "Pergolen"];
const TOPIC_TO_PRODUCT = { "Haustür": "Haustüren", "Vordach": "Vordächer", "Schiebetür": "Schiebetüren", "Garagentor": "Garagentore", "Zaun": "Zäune", "Pergola": "Pergolen" };

// Konfigurations-Nachricht → vorbefüllte Angebotsposition
const offerFromInquiry = (m) => {
  const product = PRODUCT_NAMES.includes(m.topic) ? m.topic : TOPIC_TO_PRODUCT[m.topic] || "Fenster";
  const item = {
    product, width: "1200", height: "1400", fence_length: "25", fence_height: "1,4 m",
    color: COLORS[0], options: {}, qty: 1, unit_price: 0,
  };
  const optDefs = PRODUCT_OPTIONS[product] || [];
  for (const line of (m.message || "").split("\n")) {
    const [k, ...rest] = line.split(":");
    const key = k?.trim();
    const value = rest.join(":").trim();
    if (!value) continue;
    const dimsMatch = line.match(/Maße:\s*([\d.]+)\s*×\s*([\d.]+)/);
    if (dimsMatch) { item.width = dimsMatch[1]; item.height = dimsMatch[2]; continue; }
    const fenceMatch = line.match(/Länge:\s*([\d,\.]+)\s*m\s*·\s*Höhe:\s*(.+)/);
    if (fenceMatch) { item.fence_length = fenceMatch[1]; item.fence_height = fenceMatch[2].trim(); continue; }
    if (key === "Farbe" && COLORS.includes(value)) { item.color = value; continue; }
    const def = optDefs.find((o) => o.label === key);
    if (def && def.choices.includes(value)) item.options[def.key] = value;
  }
  return {
    customer_name: m.name,
    customer_email: m.email,
    customer_address: "",
    items: [item],
    note: "50 % Anzahlung bei Auftragsbestätigung, Restzahlung nach abgeschlossener Montage.\nLieferzeit ca. 4–6 Wochen ab Aufmaß.",
    status: "entwurf",
    valid_until: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  };
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white/5 border border-white/15 rounded-xl px-5 py-3.5 text-white placeholder:text-white/35 text-sm focus:outline-none focus:border-[#e2001a] focus:bg-white/10 transition-colors duration-200";

const STATUS = [
  { key: "offen", label: "Offen", cls: "border-white/30 text-white/70" },
  { key: "in_bearbeitung", label: "In Bearbeitung", cls: "border-amber-400/50 text-amber-300" },
  { key: "erledigt", label: "Erledigt", cls: "border-emerald-400/50 text-emerald-300" },
];

const formatApiError = (detail) => {
  if (!detail) return "Anmeldung fehlgeschlagen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const StatusPicker = ({ value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {STATUS.map((s) => (
      <button
        key={s.key}
        type="button"
        data-testid={`${testid}-${s.key}`}
        onClick={() => onChange(s.key)}
        className={`px-3.5 py-1.5 rounded-full border font-display text-xs font-medium transition-colors duration-200 ${
          value === s.key ? `${s.cls} bg-white/10` : "border-white/10 text-white/35 hover:text-white/70"
        }`}
      >
        {s.label}
      </button>
    ))}
  </div>
);

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("inbox");
  const [filter, setFilter] = useState("aktive");

  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [offers, setOffers] = useState([]);
  const [offerView, setOfferView] = useState("list");
  const [editingOffer, setEditingOffer] = useState(null);
  const [printOffer, setPrintOffer] = useState(null);
  const [loading, setLoading] = useState(false);

  const [notes, setNotes] = useState({});
  const [newTask, setNewTask] = useState({ text: "", assignee: "", inquiry_id: "" });
  const [newEmp, setNewEmp] = useState({ name: "", email: "", phone: "", role: "Monteur", password: "" });
  const [empPw, setEmpPw] = useState({ id: null, password: "", repeat: "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "", repeat: "" });

  const isAdmin = user?.role === "admin";
  const isBuchhalter = user?.role === "buchhaltung";

  useEffect(() => {
    if (isBuchhalter) setTab("accounting");
  }, [isBuchhalter]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/contact`, { withCredentials: true }),
        axios.get(`${API}/tasks`, { withCredentials: true }),
      ]);
      setMessages(m.data);
      setTasks(t.data);
      setNotes(Object.fromEntries(m.data.map((x) => [x.id, x.note || ""])));
      try {
        const e = await axios.get(`${API}/employees`, { withCredentials: true });
        setEmployees(e.data);
      } catch {
        setEmployees([]);
      }
      try {
        const o = await axios.get(`${API}/offers`, { withCredentials: true });
        setOffers(o.data);
      } catch {
        setOffers([]);
      }
    } catch {
      toast.error("Daten konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadAll();
  }, [user, loadAll]);

  useEffect(() => {
    if (!printOffer) return;
    const close = () => setPrintOffer(null);
    window.addEventListener("afterprint", close);
    return () => window.removeEventListener("afterprint", close);
  }, [printOffer]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, login, { withCredentials: true });
      setUser(data);
      toast.success(`Angemeldet. Willkommen, ${data.name}!`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const doLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  const patchMessage = async (id, changes) => {
    try {
      const { data } = await axios.patch(`${API}/contact/${id}`, changes, { withCredentials: true });
      setMessages((ms) => ms.map((m) => (m.id === id ? data : m)));
      if (changes.note !== undefined) toast.success("Notiz gespeichert.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Änderung fehlgeschlagen.");
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/tasks`, newTask, { withCredentials: true });
      setTasks((ts) => [data, ...ts]);
      setNewTask({ text: "", assignee: "", inquiry_id: "" });
      toast.success("Aufgabe angelegt.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Aufgabe konnte nicht angelegt werden.");
    }
  };

  const setTaskStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/tasks/${id}`, { status }, { withCredentials: true });
      setTasks((ts) => ts.map((t) => (t.id === id ? data : t)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Status-Änderung fehlgeschlagen.");
    }
  };

  const removeTask = async (id) => {
    await axios.delete(`${API}/tasks/${id}`, { withCredentials: true }).catch(() => toast.error("Löschen fehlgeschlagen."));
    setTasks((ts) => ts.filter((t) => t.id !== id));
  };

  const addEmployee = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newEmp };
      if (!payload.password) delete payload.password;
      const { data } = await axios.post(`${API}/employees`, payload, { withCredentials: true });
      setEmployees((es) => [...es, data]);
      setNewEmp({ name: "", email: "", phone: "", role: "Monteur", password: "" });
      toast.success(data.has_login ? `${data.name} wurde mit Login angelegt.` : `${data.name} wurde hinzugefügt.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Mitarbeiter konnte nicht angelegt werden.");
    }
  };

  const removeEmployee = async (id) => {
    await axios.delete(`${API}/employees/${id}`, { withCredentials: true }).catch(() => toast.error("Löschen fehlgeschlagen."));
    setEmployees((es) => es.filter((e) => e.id !== id));
  };

  const setEmployeePassword = async (empId) => {
    if (empPw.password.length < 8) {
      toast.error("Das Passwort muss mindestens 8 Zeichen haben.");
      return;
    }
    if (empPw.password !== empPw.repeat) {
      toast.error("Die Passwörter stimmen nicht überein.");
      return;
    }
    try {
      await axios.patch(`${API}/employees/${empId}/password`, { new_password: empPw.password }, { withCredentials: true });
      toast.success("Mitarbeiter-Passwort wurde geändert.");
      setEmpPw({ id: null, password: "", repeat: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Passwort konnte nicht geändert werden.");
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.repeat) {
      toast.error("Die neuen Passwörter stimmen nicht überein.");
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: pw.current_password, new_password: pw.new_password }, { withCredentials: true });
      setPw({ current_password: "", new_password: "", repeat: "" });
      toast.success("Passwort wurde geändert.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const filteredMessages = messages.filter((m) =>
    filter === "aktive" ? m.status !== "erledigt" : filter === "erledigt" ? m.status === "erledigt" : true
  );
  const openCount = messages.filter((m) => m.status !== "erledigt").length;
  const inquiryLabel = (id) => {
    const m = messages.find((x) => x.id === id);
    return m ? `${m.name} – ${m.topic}` : null;
  };

  const TABS = isBuchhalter ? [
    { key: "accounting", label: "Buchhaltung", icon: Calculator },
    { key: "account", label: "Konto", icon: KeyRound },
  ] : [
    { key: "inbox", label: `Postfach${openCount ? ` (${openCount})` : ""}`, icon: Inbox },
    { key: "tasks", label: "Aufgaben", icon: ListTodo },
    ...(isAdmin ? [
      { key: "offers", label: "Angebote", icon: FileText },
      { key: "accounting", label: "Buchhaltung", icon: Calculator },
      { key: "employees", label: "Mitarbeiter", icon: Users },
    ] : []),
    { key: "account", label: "Konto", icon: KeyRound },
  ];

  const deleteOffer = async (id) => {
    await axios.delete(`${API}/offers/${id}`, { withCredentials: true }).catch(() => toast.error("Löschen fehlgeschlagen."));
    setOffers((os) => os.filter((o) => o.id !== id));
  };

  const triggerPrint = (offer) => {
    setPrintOffer(offer);
    setTimeout(() => window.print(), 400);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#14171a] text-white">
      <div className={`${tab === "accounting" ? "max-w-6xl" : "max-w-4xl"} mx-auto px-5 py-10`}>
        <div className="flex items-center justify-between mb-10">
          <img src={IMAGES.logo} alt="Ringel Fenstertechnik" className="h-16 w-auto" />
          <Link to="/" data-testid="admin-back" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors">
            <ArrowLeft size={15} /> Zur Website
          </Link>
        </div>

        {user === undefined ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin text-white/50" size={28} /></div>
        ) : !user ? (
          <form data-testid="admin-login-form" onSubmit={submitLogin} className="max-w-md mx-auto bg-[#1e2226] border border-white/10 rounded-2xl p-8 sm:p-10">
            <span className="mx-auto w-14 h-14 rounded-full bg-[#e2001a]/15 border border-[#e2001a]/40 flex items-center justify-center text-[#e2001a] mb-6">
              <Lock size={22} />
            </span>
            <h1 className="font-display font-bold text-2xl text-center mb-2">Anfragen-Postfach</h1>
            <p className="text-white/50 text-sm text-center mb-8">Für Inhaber & Team – bitte anmelden.</p>
            <div className="space-y-4">
              <input data-testid="admin-email" type="email" required value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} placeholder="E-Mail" className={inputCls} />
              <input data-testid="admin-password" type="password" required value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} placeholder="Passwort" className={inputCls} />
              {error && <p data-testid="admin-login-error" className="text-[#ff4d5e] text-sm">{error}</p>}
              <button data-testid="admin-login-submit" type="submit" disabled={busy} className="w-full bg-[#e2001a] hover:bg-[#b80016] disabled:opacity-60 font-display font-semibold py-4 rounded-full transition-colors">
                {busy ? "Prüfe …" : "Anmelden"}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
              <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`admin-tab-${t.key}`}
                    onClick={() => setTab(t.key)}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-display text-sm font-medium border transition-colors duration-200 ${
                      tab === t.key ? "bg-[#e2001a] border-[#e2001a] text-white" : "border-white/20 text-white/60 hover:text-white"
                    }`}
                  >
                    <t.icon size={15} /> {t.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <button data-testid="admin-refresh" onClick={loadAll} className="inline-flex items-center gap-2 border border-white/25 hover:border-white px-5 py-2.5 rounded-full text-sm font-display transition-colors">
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aktualisieren
                </button>
                <button data-testid="admin-logout" onClick={doLogout} className="inline-flex items-center gap-2 border border-white/25 hover:border-white px-5 py-2.5 rounded-full text-sm font-display transition-colors">
                  <LogOut size={14} /> Abmelden
                </button>
              </div>
            </div>

            {!isAdmin && (
              <p data-testid="employee-hint" className="text-white/45 text-sm mb-6">
                Angemeldet als {user.name} – {isBuchhalter ? "du siehst den Buchhaltungs-Bereich." : "du siehst nur deine zugewiesenen Anfragen und Aufgaben."}
              </p>
            )}

            {tab === "inbox" && (
              <div>
                <div className="flex gap-2 mb-6" data-testid="inbox-filter">
                  {[
                    { key: "aktive", label: `Aktive (${openCount})` },
                    { key: "erledigt", label: `Erledigt (${messages.length - openCount})` },
                    { key: "alle", label: `Alle (${messages.length})` },
                  ].map((f) => (
                    <button
                      key={f.key}
                      data-testid={`filter-${f.key}`}
                      onClick={() => setFilter(f.key)}
                      className={`px-4 py-2 rounded-full border font-display text-xs font-medium transition-colors ${
                        filter === f.key ? "bg-white text-[#14171a] border-white" : "border-white/20 text-white/55 hover:text-white"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                {filteredMessages.length === 0 && !loading ? (
                  <div data-testid="admin-empty" className="bg-[#1e2226] border border-white/10 rounded-2xl p-14 text-center text-white/50">
                    {filter === "aktive" ? "Keine offenen Anfragen – alles erledigt." : "Keine Anfragen in dieser Ansicht."}
                  </div>
                ) : (
                  <div data-testid="admin-messages" className="space-y-4">
                    {filteredMessages.map((m) => {
                      const linked = tasks.filter((t) => t.inquiry_id === m.id);
                      return (
                        <article key={m.id} data-testid={`admin-message-${m.id}`} className={`bg-[#1e2226] border rounded-2xl p-6 transition-colors ${m.status === "erledigt" ? "border-white/5 opacity-60" : "border-white/10 hover:border-white/25"}`}>
                          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                            <div>
                              <h2 className="font-display font-semibold text-lg">{m.name}</h2>
                              <div className="flex flex-wrap gap-4 mt-1 text-sm text-white/55">
                                <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 hover:text-white transition-colors"><Mail size={13} className="text-[#e2001a]" />{m.email}</a>
                                {m.phone && <a href={`tel:${m.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-white transition-colors"><Phone size={13} className="text-[#e2001a]" />{m.phone}</a>}
                              </div>
                            </div>
                            <div className="text-right">
                              <span className="inline-block font-display text-xs uppercase tracking-widest bg-[#e2001a]/15 text-[#ff8090] border border-[#e2001a]/40 rounded-full px-3 py-1">{m.topic}</span>
                              <p className="text-white/35 text-xs mt-2">{new Date(m.timestamp).toLocaleString("de-DE")}</p>
                            </div>
                          </div>
                          <p className="text-white/75 text-sm leading-relaxed whitespace-pre-line border-t border-white/10 pt-4">{m.message}</p>

                          {linked.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-2" data-testid={`msg-linked-tasks-${m.id}`}>
                              {linked.map((t) => (
                                <span key={t.id} className="inline-flex items-center gap-1.5 text-xs bg-white/5 border border-white/15 rounded-full px-3 py-1.5 text-white/60">
                                  <Link2 size={11} className="text-[#e2001a]" /> {t.text}
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="mt-5 pt-4 border-t border-white/10 grid sm:grid-cols-2 gap-5">
                            <div>
                              <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-display mb-2.5">Status</p>
                              <StatusPicker testid={`msg-status-${m.id}`} value={m.status} onChange={(s) => patchMessage(m.id, { status: s })} />
                            </div>
                            {isAdmin && (
                              <div>
                                <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-display mb-2.5">Zuständig</p>
                                <select
                                  data-testid={`msg-assignee-${m.id}`}
                                  value={m.assignee || ""}
                                  onChange={(e) => patchMessage(m.id, { assignee: e.target.value })}
                                  className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-[#1e2226] !py-2.5`}
                                >
                                  <option value="">Niemand</option>
                                  {employees.map((emp) => <option key={emp.id} value={emp.name}>{emp.name}</option>)}
                                </select>
                              </div>
                            )}
                          </div>

                          <div className="mt-5">
                            <p className="text-white/40 text-xs uppercase tracking-[0.2em] font-display mb-2.5">Notiz</p>
                            {isAdmin ? (
                              <div className="flex gap-3">
                                <textarea
                                  data-testid={`msg-note-${m.id}`}
                                  rows={2}
                                  value={notes[m.id] ?? ""}
                                  onChange={(e) => setNotes({ ...notes, [m.id]: e.target.value })}
                                  placeholder="Interne Notiz zu dieser Anfrage …"
                                  className={`${inputCls} resize-none`}
                                />
                                <button
                                  data-testid={`msg-note-save-${m.id}`}
                                  onClick={() => patchMessage(m.id, { note: notes[m.id] ?? "" })}
                                  className="shrink-0 self-end inline-flex items-center gap-2 border border-white/25 hover:border-white px-4 py-2.5 rounded-xl text-sm font-display transition-colors"
                                >
                                  <Save size={14} /> Speichern
                                </button>
                              </div>
                            ) : (
                              m.note && <p className="text-white/60 text-sm bg-white/5 rounded-xl px-4 py-3">{m.note}</p>
                            )}
                          </div>

                          <div className="mt-5 flex flex-wrap justify-end gap-3">
                            {isAdmin && (
                              <button
                                data-testid={`msg-to-offer-${m.id}`}
                                onClick={() => { setEditingOffer(offerFromInquiry(m)); setOfferView("edit"); setTab("offers"); }}
                                className="inline-flex items-center gap-2 border border-[#e2001a]/50 text-[#ff8090] hover:bg-[#e2001a]/10 px-5 py-2.5 rounded-full text-sm font-display font-medium transition-colors"
                              >
                                <FileText size={14} /> Angebot erstellen
                              </button>
                            )}
                            <a
                              data-testid={`msg-reply-${m.id}`}
                              href={`mailto:${m.email}?subject=${encodeURIComponent(`Ihre Anfrage: ${m.topic} – Ringel Fenstertechnik`)}&body=${encodeURIComponent(`Guten Tag ${m.name},\n\nvielen Dank für Ihre Anfrage.\n\n\n\nMit freundlichen Grüßen\nAndreas Ringel\nRingel Fenstertechnik · ${CONTACT.phone}`)}`}
                              className="inline-flex items-center gap-2 bg-[#e2001a] hover:bg-[#b80016] px-5 py-2.5 rounded-full text-sm font-display font-medium transition-colors"
                            >
                              <Reply size={14} /> Per E-Mail beantworten
                            </a>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === "tasks" && (
              <div>
                {isAdmin && (
                  <form data-testid="task-form" onSubmit={addTask} className="bg-[#1e2226] border border-white/10 rounded-2xl p-6 mb-6 space-y-3">
                    <input data-testid="task-text" required value={newTask.text} onChange={(e) => setNewTask({ ...newTask, text: e.target.value })} placeholder="Neue Aufgabe, z. B. „Angebot Müller rausschicken“ …" className={inputCls} />
                    <div className="flex flex-wrap gap-3">
                      <select data-testid="task-assignee" value={newTask.assignee} onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })} className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-[#1e2226] !w-auto`}>
                        <option value="">Niemand zugewiesen</option>
                        {employees.map((emp) => <option key={emp.id} value={emp.name}>{emp.name}</option>)}
                      </select>
                      <select data-testid="task-inquiry" value={newTask.inquiry_id} onChange={(e) => setNewTask({ ...newTask, inquiry_id: e.target.value })} className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-[#1e2226] !w-auto`}>
                        <option value="">Keine Anfrage verknüpft</option>
                        {messages.map((m) => <option key={m.id} value={m.id}>{m.name} – {m.topic}</option>)}
                      </select>
                      <button data-testid="task-add" type="submit" className="inline-flex items-center gap-2 bg-[#e2001a] hover:bg-[#b80016] px-6 py-3.5 rounded-xl font-display font-semibold text-sm transition-colors">
                        <Plus size={15} /> Hinzufügen
                      </button>
                    </div>
                  </form>
                )}

                {tasks.length === 0 ? (
                  <div data-testid="tasks-empty" className="bg-[#1e2226] border border-white/10 rounded-2xl p-14 text-center text-white/50">
                    {isAdmin ? "Keine Aufgaben – lege oben die erste an." : "Dir sind aktuell keine Aufgaben zugewiesen."}
                  </div>
                ) : (
                  <div data-testid="tasks-list" className="space-y-3">
                    {tasks.map((t) => {
                      const linkedLabel = t.inquiry_id ? inquiryLabel(t.inquiry_id) : null;
                      return (
                        <div key={t.id} data-testid={`task-${t.id}`} className={`bg-[#1e2226] border border-white/10 rounded-2xl p-5 flex flex-wrap items-center gap-4 ${t.status === "erledigt" ? "opacity-60" : ""}`}>
                          <div className="flex-1 min-w-[200px]">
                            <p className={`font-display font-medium ${t.status === "erledigt" ? "line-through text-white/45" : ""}`}>{t.text}</p>
                            <div className="flex flex-wrap gap-3 mt-1 text-xs text-white/40">
                              {t.assignee && <span>Zuständig: {t.assignee}</span>}
                              {linkedLabel && (
                                <span data-testid={`task-inquiry-label-${t.id}`} className="inline-flex items-center gap-1 text-white/60">
                                  <Link2 size={11} className="text-[#e2001a]" /> {linkedLabel}
                                </span>
                              )}
                            </div>
                          </div>
                          <StatusPicker testid={`task-status-${t.id}`} value={t.status} onChange={(s) => setTaskStatus(t.id, s)} />
                          {isAdmin && (
                            <button data-testid={`task-delete-${t.id}`} onClick={() => removeTask(t.id)} className="text-white/30 hover:text-[#ff4d5e] transition-colors p-2" aria-label="Aufgabe löschen">
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === "offers" && isAdmin && (
              offerView === "edit" ? (
                <OfferEditor
                  offer={editingOffer}
                  onClose={() => { setOfferView("list"); setEditingOffer(null); }}
                  onSaved={() => { loadAll(); }}
                  onPrint={(o) => triggerPrint(o)}
                />
              ) : (
                <div>
                  <button
                    data-testid="offer-new"
                    onClick={() => { setEditingOffer(null); setOfferView("edit"); }}
                    className="inline-flex items-center gap-2 bg-[#e2001a] hover:bg-[#b80016] px-6 py-3.5 rounded-xl font-display font-semibold text-sm transition-colors mb-6"
                  >
                    <Plus size={15} /> Neues Angebot
                  </button>
                  {offers.length === 0 ? (
                    <div data-testid="offers-empty" className="bg-[#1e2226] border border-white/10 rounded-2xl p-14 text-center text-white/50">
                      Noch keine Angebote – lege oben das erste an.
                    </div>
                  ) : (
                    <div data-testid="offers-list" className="space-y-3">
                      {offers.map((o) => {
                        const total = o.items.reduce((s, it) => s + it.qty * it.unit_price, 0);
                        return (
                          <div key={o.id} data-testid={`offer-${o.id}`} className="bg-[#1e2226] border border-white/10 rounded-2xl p-5 flex flex-wrap items-center gap-4">
                            <div className="flex-1 min-w-[220px]">
                              <p className="font-display font-semibold">{o.number} · {o.customer_name}</p>
                              <p className="text-white/45 text-xs mt-1">
                                {o.items.length} {o.items.length === 1 ? "Position" : "Positionen"} · {new Date(o.timestamp).toLocaleDateString("de-DE")} ·{" "}
                                <span className="text-[#ff8090]">{o.status}</span>
                              </p>
                            </div>
                            <p className="font-display font-bold text-lg text-white">{total.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €</p>
                            <div className="flex gap-2">
                              <button data-testid={`offer-print-${o.id}`} onClick={() => triggerPrint(o)} className="inline-flex items-center gap-2 border border-white/25 hover:border-white px-4 py-2.5 rounded-xl text-sm font-display transition-colors">
                                <Printer size={14} /> Drucken
                              </button>
                              <button data-testid={`offer-edit-${o.id}`} onClick={() => { setEditingOffer(o); setOfferView("edit"); }} className="inline-flex items-center gap-2 border border-white/25 hover:border-white px-4 py-2.5 rounded-xl text-sm font-display transition-colors">
                                <Pencil size={14} /> Bearbeiten
                              </button>
                              <button data-testid={`offer-delete-${o.id}`} onClick={() => deleteOffer(o.id)} className="text-white/30 hover:text-[#ff4d5e] transition-colors p-2" aria-label="Angebot löschen">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )
            )}

            {tab === "accounting" && (isAdmin || isBuchhalter) && (
              <Accounting isAdmin={isAdmin} />
            )}

            {tab === "employees" && isAdmin && (
              <div>
                <form data-testid="employee-form" onSubmit={addEmployee} className="bg-[#1e2226] border border-white/10 rounded-2xl p-6 mb-6 grid sm:grid-cols-2 gap-4">
                  <input data-testid="emp-name" required value={newEmp.name} onChange={(e) => setNewEmp({ ...newEmp, name: e.target.value })} placeholder="Name *" className={inputCls} />
                  <input data-testid="emp-email" required type="email" value={newEmp.email} onChange={(e) => setNewEmp({ ...newEmp, email: e.target.value })} placeholder="E-Mail *" className={inputCls} />
                  <input data-testid="emp-phone" value={newEmp.phone} onChange={(e) => setNewEmp({ ...newEmp, phone: e.target.value })} placeholder="Telefon (optional)" className={inputCls} />
                  <select data-testid="emp-role" value={newEmp.role} onChange={(e) => setNewEmp({ ...newEmp, role: e.target.value })} className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-[#1e2226]`}>
                    {["Monteur", "Büro", "Vertrieb", "Azubi", "Buchhaltung"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <input data-testid="emp-password" type="text" value={newEmp.password} onChange={(e) => setNewEmp({ ...newEmp, password: e.target.value })} placeholder="Login-Passwort (optional, mind. 8 Zeichen)" className={inputCls} />
                  <button data-testid="emp-add" type="submit" className="inline-flex items-center justify-center gap-2 bg-[#e2001a] hover:bg-[#b80016] px-6 rounded-xl font-display font-semibold text-sm transition-colors">
                    <Plus size={15} /> Mitarbeiter anlegen
                  </button>
                  <p className="sm:col-span-2 text-white/40 text-xs">Mit Login-Passwort bekommt der Mitarbeiter ein eigenes Konto. Rolle „Buchhaltung" sieht nur die Buchhaltung (Belege, Ein-/Ausgaben, Exporte) – alle anderen Rollen nur ihre zugewiesenen Anfragen und Aufgaben.</p>
                </form>

                {employees.length === 0 ? (
                  <div data-testid="employees-empty" className="bg-[#1e2226] border border-white/10 rounded-2xl p-14 text-center text-white/50">Noch keine Mitarbeiter angelegt.</div>
                ) : (
                  <div data-testid="employees-list" className="grid sm:grid-cols-2 gap-4">
                    {employees.map((emp) => (
                      <div key={emp.id} data-testid={`employee-${emp.id}`} className="bg-[#1e2226] border border-white/10 rounded-2xl p-5">
                        <div className="flex items-center gap-4">
                          <span className="w-12 h-12 shrink-0 rounded-full bg-[#e2001a]/15 border border-[#e2001a]/40 flex items-center justify-center font-display font-bold text-[#ff8090]">
                            {emp.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-display font-semibold truncate flex items-center gap-2">
                              {emp.name}
                              {emp.has_login && <span data-testid={`emp-login-badge-${emp.id}`} className="text-[10px] font-medium uppercase tracking-widest bg-emerald-400/15 text-emerald-300 border border-emerald-400/40 rounded-full px-2 py-0.5">Login aktiv</span>}
                            </p>
                            <p className="text-white/45 text-xs truncate">{emp.role} · {emp.email}{emp.phone ? ` · ${emp.phone}` : ""}</p>
                          </div>
                          {emp.has_login && (
                            <button
                              data-testid={`emp-pw-${emp.id}`}
                              onClick={() => setEmpPw(empPw.id === emp.id ? { id: null, password: "", repeat: "" } : { id: emp.id, password: "", repeat: "" })}
                              className="text-white/40 hover:text-white transition-colors p-2"
                              title="Passwort ändern"
                              aria-label="Passwort ändern"
                            >
                              <KeyRound size={16} />
                            </button>
                          )}
                          <button data-testid={`emp-delete-${emp.id}`} onClick={() => removeEmployee(emp.id)} className="text-white/30 hover:text-[#ff4d5e] transition-colors p-2" aria-label="Mitarbeiter löschen">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        {empPw.id === emp.id && (
                          <div data-testid={`emp-pw-form-${emp.id}`} className="mt-4 pt-4 border-t border-white/10 space-y-3">
                            <input
                              data-testid={`emp-pw-new-${emp.id}`}
                              type="password"
                              value={empPw.password}
                              onChange={(e) => setEmpPw({ ...empPw, password: e.target.value })}
                              placeholder="Neues Passwort (mind. 8 Zeichen)"
                              className={inputCls}
                            />
                            <input
                              data-testid={`emp-pw-repeat-${emp.id}`}
                              type="password"
                              value={empPw.repeat}
                              onChange={(e) => setEmpPw({ ...empPw, repeat: e.target.value })}
                              placeholder="Neues Passwort wiederholen"
                              className={inputCls}
                            />
                            <div className="flex gap-3">
                              <button data-testid={`emp-pw-save-${emp.id}`} onClick={() => setEmployeePassword(emp.id)} className="inline-flex items-center gap-2 bg-[#e2001a] hover:bg-[#b80016] px-5 py-2.5 rounded-xl font-display font-semibold text-sm transition-colors">
                                <Save size={14} /> Passwort speichern
                              </button>
                              <button data-testid={`emp-pw-cancel-${emp.id}`} onClick={() => setEmpPw({ id: null, password: "", repeat: "" })} className="inline-flex items-center gap-2 border border-white/25 hover:border-white px-5 py-2.5 rounded-xl text-sm font-display transition-colors">
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "account" && (
              <form data-testid="password-form" onSubmit={changePassword} className="max-w-md bg-[#1e2226] border border-white/10 rounded-2xl p-8">
                <h2 className="font-display font-semibold text-xl mb-2">Passwort ändern</h2>
                <p className="text-white/50 text-sm mb-6">Angemeldet als {user.name || user.email}</p>
                <div className="space-y-4">
                  <input data-testid="pw-current" type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Aktuelles Passwort" className={inputCls} />
                  <input data-testid="pw-new" type="password" required minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="Neues Passwort (mind. 8 Zeichen)" className={inputCls} />
                  <input data-testid="pw-repeat" type="password" required minLength={8} value={pw.repeat} onChange={(e) => setPw({ ...pw, repeat: e.target.value })} placeholder="Neues Passwort wiederholen" className={inputCls} />
                  <button data-testid="pw-submit" type="submit" className="w-full bg-[#e2001a] hover:bg-[#b80016] font-display font-semibold py-3.5 rounded-full transition-colors">
                    Passwort speichern
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
      <p className="text-center text-white/25 text-xs pb-10">{CONTACT.owner} · Ringel Fenstertechnik</p>

      {printOffer && (
        <div className="print-overlay">
          <OfferSheet offer={printOffer} />
        </div>
      )}
    </div>
  );
}
